import React from 'react';
import AnimatedSection from './AnimatedSection';
import BackToTop from './BackToTop';

const icons = {
  phone: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  ),
  mail: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  ),
  pin: (
    <>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </>
  ),
  whatsapp: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 10.5h.01M12 10.5h.01M16 10.5h.01M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" />
  ),
};

const Icon: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    {children}
  </svg>
);

const channels = [
  {
    icon: icons.phone,
    title: 'Call us',
    value: '+234 814 584 0203',
    hint: 'Mon–Sat, 9am – 6pm WAT',
    link: 'tel:+2348145840203',
    cta: 'Call now',
  },
  {
    icon: icons.mail,
    title: 'Email us',
    value: 'admin@nexasolutra.com',
    hint: 'We reply within one business day',
    link: 'mailto:admin@nexasolutra.com',
    cta: 'Send an email',
  },
  {
    icon: icons.whatsapp,
    title: 'WhatsApp',
    value: '+234 814 584 0203',
    hint: 'Fastest way to reach us',
    link: 'https://wa.me/2348145840203',
    cta: 'Start a chat',
    external: true,
  },
];

const socialLinks = [
  {
    name: 'Instagram',
    url: 'https://www.instagram.com/nexasolutra?igsh=MWgxY2VjcWJjOXluNQ%3D%3D&utm_source=qr',
    path: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z',
  },
  {
    name: 'LinkedIn',
    url: 'https://www.linkedin.com/company/nexasolutra/',
    path: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z',
  },
  {
    name: 'Facebook',
    url: 'https://www.facebook.com/share/1K9mkDUxSB/?mibextid=wwXIfr',
    path: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z',
  },
];

/** What a first call actually covers — removes the "what will they ask me" hesitation. */
const agenda = [
  'How your operations run today, and where the leaks are',
  'Which parts of Zoxohu or ISEMMA fit your business',
  'Whether a custom system is the better answer',
  'Timeline, rollout, and what it costs',
];

const Contact: React.FC = () => {
  return (
    <div className="relative">
      {/* ---------------------------------------------------------- */}
      {/* Hero                                                        */}
      {/* ---------------------------------------------------------- */}
      <section id="contact" className="px-4 sm:px-6 lg:px-8 pt-28 md:pt-36 pb-14 md:pb-20">
        <div className="max-w-7xl mx-auto">
          <AnimatedSection animation="slide-up">
            <div className="max-w-3xl">
              <p className="section-label">Contact</p>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-tight text-balance leading-[1.08] mb-6">
                Let&apos;s talk about how your{' '}
                <span className="text-primary-500">business runs.</span>
              </h1>
              <p className="text-lg md:text-xl text-muted leading-relaxed">
                Tell us about your hospitality or commerce business. We&apos;ll show you where
                intelligent software removes manual work, protects margin, and makes room to grow.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ---------------------------------------------------------- */}
      {/* Channels + agenda                                           */}
      {/* ---------------------------------------------------------- */}
      <section className="px-4 sm:px-6 lg:px-8 pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-8 items-start">
          {/* Channels */}
          <div className="lg:col-span-7 space-y-4">
            {channels.map((channel, i) => (
              <AnimatedSection key={channel.title} animation="slide-up" delay={i * 70}>
                <a
                  href={channel.link}
                  {...(channel.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                  className="group flex items-center gap-5 p-6 md:p-7 rounded-2xl border border-surface-border bg-surface-card hover:border-primary-500/40 hover:bg-surface-hover transition-all duration-300"
                >
                  <span className="w-12 h-12 rounded-xl border border-surface-border bg-surface text-primary-500 flex items-center justify-center shrink-0 group-hover:border-primary-500/40 group-hover:bg-primary-500/10 transition-all duration-300">
                    <Icon className="w-5 h-5">{channel.icon}</Icon>
                  </span>

                  <div className="min-w-0 flex-1">
                    <p className="text-xs uppercase tracking-widest text-muted mb-1.5">
                      {channel.title}
                    </p>
                    <p className="font-display text-lg md:text-xl font-semibold text-white truncate">
                      {channel.value}
                    </p>
                    <p className="text-sm text-muted mt-1">{channel.hint}</p>
                  </div>

                  <span
                    className="text-primary-500 text-xl shrink-0 transition-transform duration-300 group-hover:translate-x-1"
                    aria-hidden
                  >
                    →
                  </span>
                </a>
              </AnimatedSection>
            ))}

            {/* Office */}
            <AnimatedSection animation="slide-up" delay={210}>
              <div className="rounded-2xl border border-surface-border bg-surface-card overflow-hidden">
                <div className="flex items-start gap-5 p-6 md:p-7">
                  <span className="w-12 h-12 rounded-xl border border-surface-border bg-surface text-primary-500 flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5">{icons.pin}</Icon>
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs uppercase tracking-widest text-muted mb-1.5">Office</p>
                    <p className="font-display text-lg font-semibold text-white leading-snug">
                      Flt 7 by Chief/Pal, Along Gbuduwyi Kabusa
                    </p>
                    <p className="text-sm text-muted mt-1">
                      Opposite Animal Science · Abuja, Nigeria
                    </p>
                    <a
                      href="https://www.google.com/maps/search/?api=1&query=Gbuduwyi+Kabusa+Abuja+Nigeria"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-medium text-primary-500 hover:text-primary-400 mt-4 transition-colors"
                    >
                      Get directions <span aria-hidden>→</span>
                    </a>
                  </div>
                </div>

                {/* Abstract map strip — suggests place without loading a third-party map */}
                <div className="relative h-24 border-t border-surface-border bg-surface overflow-hidden" aria-hidden>
                  <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 96" preserveAspectRatio="none">
                    <g stroke="currentColor" className="text-surface-border" strokeWidth="1">
                      <path d="M0 30 H400 M0 66 H400 M60 0 V96 M150 0 V96 M260 0 V96 M340 0 V96" />
                    </g>
                    <path d="M0 66 L150 66 L150 30 L400 30" fill="none" stroke="#16A34A" strokeWidth="2" strokeOpacity="0.5" />
                    <circle cx="150" cy="30" r="5" fill="#16A34A" />
                    <circle cx="150" cy="30" r="11" fill="none" stroke="#16A34A" strokeOpacity="0.35" strokeWidth="1.5" />
                  </svg>
                </div>
              </div>
            </AnimatedSection>
          </div>

          {/* Agenda + social */}
          <div className="lg:col-span-5 space-y-5 lg:sticky lg:top-28">
            <AnimatedSection animation="slide-up" delay={100}>
              <div className="rounded-2xl border border-surface-border bg-surface-card p-7 md:p-8">
                <p className="text-xs uppercase tracking-[0.2em] text-primary-500 mb-5">
                  What a first call covers
                </p>
                <ul className="space-y-4 mb-8">
                  {agenda.map((item, i) => (
                    <li key={item} className="flex items-start gap-3.5">
                      <span className="w-6 h-6 rounded-full border border-primary-500/40 bg-primary-500/10 text-primary-400 text-[11px] font-medium flex items-center justify-center shrink-0 mt-0.5">
                        {i + 1}
                      </span>
                      <span className="text-sm text-white/85 leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="https://wa.me/2348145840203"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary w-full"
                >
                  Book a Strategy Session
                </a>
                <p className="text-xs text-muted text-center mt-4">
                  No obligation. Bring your questions.
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection animation="slide-up" delay={160}>
              <div className="rounded-2xl border border-surface-border bg-surface-card p-7">
                <p className="text-xs uppercase tracking-widest text-muted mb-4">Follow along</p>
                <div className="flex gap-3">
                  {socialLinks.map((social) => (
                    <a
                      key={social.name}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-11 h-11 rounded-xl border border-surface-border bg-surface flex items-center justify-center text-muted hover:text-primary-500 hover:border-primary-500/40 transition-all duration-300"
                      aria-label={social.name}
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d={social.path} />
                      </svg>
                    </a>
                  ))}
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <BackToTop />
    </div>
  );
};

export default Contact;
